import { type SyntheticEvent, useCallback, useRef, useState } from 'react'

export const useAudioClipPlayer = () => {
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const [audioTime, setAudioTime] = useState<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackRate, setPlaybackRateState] = useState(1)
  const playbackRateRef = useRef(1)
  const clipEndRef = useRef<number | null>(null)
  const programmaticSeekRef = useRef(false)

  const startPlayback = useCallback((audio: HTMLAudioElement) => {
    void audio.play().catch(() => setIsPlaying(false))
  }, [])

  const playFrom = useCallback(
    (startMs: number, endMs?: number) => {
      const a = audioElementRef.current
      if (!a) return
      clipEndRef.current = endMs ?? null
      programmaticSeekRef.current = true
      a.currentTime = Math.max(0, startMs / 1000)
      setAudioTime(startMs)
      startPlayback(a)
    },
    [startPlayback]
  )

  const seekTo = useCallback((startMs: number) => {
    const a = audioElementRef.current
    if (!a) return
    clipEndRef.current = null
    programmaticSeekRef.current = true
    a.currentTime = Math.max(0, startMs / 1000)
    setAudioTime(startMs)
  }, [])

  const togglePlayback = useCallback(
    (startMs?: number, endMs?: number) => {
      const a = audioElementRef.current
      if (!a) return
      if (a.paused) {
        if (startMs != null && clipEndRef.current == null) {
          playFrom(startMs, endMs)
          return
        }
        startPlayback(a)
        return
      }
      a.pause()
    },
    [playFrom, startPlayback]
  )

  const pause = useCallback(() => audioElementRef.current?.pause(), [])

  const setPlaybackRate = useCallback((rate: number) => {
    playbackRateRef.current = rate
    setPlaybackRateState(rate)
    if (audioElementRef.current) audioElementRef.current.playbackRate = rate
  }, [])

  const onTimeUpdate = useCallback((e: SyntheticEvent<HTMLAudioElement>) => {
    const a = e.currentTarget
    const ms = a.currentTime * 1000
    setAudioTime(ms)
    if (clipEndRef.current != null && ms >= clipEndRef.current) {
      clipEndRef.current = null
      a.pause()
    }
  }, [])

  const onSeeked = useCallback(() => {
    if (programmaticSeekRef.current) programmaticSeekRef.current = false
    else clipEndRef.current = null
  }, [])

  const onPlay = useCallback((event: SyntheticEvent<HTMLAudioElement>) => {
    setAudioTime(event.currentTarget.currentTime * 1000)
    setIsPlaying(true)
  }, [])
  const onPause = useCallback(() => setIsPlaying(false), [])
  const onEnded = useCallback(() => {
    clipEndRef.current = null
    setIsPlaying(false)
  }, [])
  const onStopped = useCallback(() => {
    clipEndRef.current = null
    setIsPlaying(false)
    setAudioTime(null)
  }, [])

  const audioRef = useCallback((audio: HTMLAudioElement | null) => {
    const previous = audioElementRef.current
    if (previous && previous !== audio) {
      previous.pause()
      previous.removeAttribute('src')
      previous.load()
    }
    audioElementRef.current = audio
    if (audio) audio.playbackRate = playbackRateRef.current
  }, [])

  return {
    audioRef,
    audioTime,
    isPlaying,
    pause,
    playbackRate,
    playFrom,
    seekTo,
    setPlaybackRate,
    togglePlayback,
    onTimeUpdate,
    onSeeked,
    onPlay,
    onPause,
    onEnded,
    onStopped,
  }
}
